/**
 * i18n-Gerüst — EN ist Primärsprache (global-first), DE vollständig paritätisch.
 * Spieler-Terminologie folgt der Lore-Bibel (docs/STORY-LINE-EN.md).
 *
 * Seit Batch 0.5.0 ist die EN↔DE-Schlüssel-Parität eine test-erzwungene
 * EIGENSCHAFT über den GESAMTEN Katalog (tests/unit/i18n-parity.test.ts,
 * `EN_ONLY_EXCEPTIONS` ist leer): JEDER EN-Key trägt eine DE-Zeile. `t()` fällt
 * dennoch defensiv auf die EN-Zeile (und zuletzt den Key selbst) zurück.
 *
 * Reiner String-Katalog (keine Interpolation) — dynamische Werte (Kosten,
 * Stückzahlen, Pro-Kopf-Gold) setzt der Aufrufer zusammen; Templates mit
 * `{n}`-Platzhalter füllt `format()`.
 */

export type Locale = 'en' | 'de';
export const SUPPORTED_LOCALES: readonly Locale[] = ['en', 'de'];
export const DEFAULT_LOCALE: Locale = 'en';

type Dictionary = Record<string, string>;

const en: Dictionary = {
  // ── Landing ──
  'landing.tagline': 'Real-time tower defense on the open sea — defend your island, plunder theirs.',
  'landing.status': 'Foundation phase — the tides are rising.',
  'landing.play': 'Play',
  // A-L2/A-Q3 (Batch 0.15.0): the primary CTA is nominal — no urgency/scarcity/countdown.
  // Quick Play (F1): one click raises a standard co-op harbor; „Choose your course"
  // is the subordinate text link to the full /play config.
  'landing.play_now': 'Play now',
  'landing.play_now_sub': 'One click — your harbor rises. No account, no download.',
  'landing.play_choose': 'Choose your course — mode, difficulty, private',
  'landing.difficulty_label': 'Difficulty',
  'landing.join_heading': 'Join with a room code',
  'landing.join_sub': 'Got a code from a friend? Drop anchor here.',
  'landing.join_code_label': 'Room code',
  'landing.join_button': 'Join',
  // Runden-Ticker (Batch 0.8.0, A-T5): Bestands-/Vergangenheitsform, NIE „now/
  // live/online", keine Bevölkerungs-Behauptung, kein CTA-Verb. {n} = exakte Zahl.
  'landing.rounds_ticker': '{n} battles fought on the open sea.',

  // ── Captain's logbook (F5, Batch 0.14.0 — device-local, plain past-tense, A7:
  //    no CTA verb, no comparison to others, no streak/expiry) ──
  'landing.log_heading': 'Your logbook',
  'landing.log_played': 'battles fought',
  'landing.log_won': 'won',
  'landing.log_bestwave': 'Best wave:',

  // ── /play — „Choose your course" step (Batch 0.8.0) ──
  'play.heading': 'Choose your course',
  'play.sub': 'Set the mode, pick the weather, then raise your harbor.',
  'play.start_button': 'Raise the harbor',
  'play.back': 'Back to shore',
  // Turnstile reject notices (Batch 0.11.0, A-TS4) — plain tone, no bot blame.
  // `invalid` = retry framing; `missing` = likely-persistent + the open join path.
  'play.notice.captcha_invalid': 'The security check did not finish in time — please raise the harbor again.',
  'play.notice.captcha_missing':
    'The security check could not load (a browser extension may be blocking it). Joining a crew by room code stays open.',

  // ── Mode selection (Batch 0.5.0 — GAME_DESIGN §15) ──
  'landing.mode_label': 'Mode',
  'mode.coop_survival.name': 'Harbor Defense',
  'mode.coop_survival.desc': 'Team up and hold the harbor against the storm — up to four defenders.',
  'mode.coop_survival.badge': 'Co-op',
  'mode.pvp_duel.name': 'Plunder War',
  'mode.pvp_duel.desc': 'Two mirror islands, one duel — outlast your rival and plunder their coast.',
  'mode.pvp_duel.badge': '1v1',
  'mode.tide_straits.name': 'Tide Straits',
  'mode.tide_straits.desc': 'Wall off the shoals and choke the fleets as the tide turns — up to four wardens.',
  'mode.tide_straits.badge': 'Straits',

  // ── Difficulty (Lobby + Landing) ──
  'difficulty.light.name': 'Light Breeze',
  'difficulty.light.desc': 'Gentler fleets, a sturdier island — a calm first voyage.',
  'difficulty.stiff.name': 'Stiff Breeze',
  'difficulty.stiff.desc': 'The reference challenge — balanced raids, standard hull.',
  'difficulty.hurricane.name': 'Hurricane',
  'difficulty.hurricane.desc': 'Relentless waves, a brittle island — for hardened harbor masters.',

  // ── Room-not-found ──
  'room.notfound.title': 'That cove is empty',
  'room.notfound.body': 'No harbor answers to that code — it may have sailed off, or never existed.',
  'room.notfound.back': 'Back to shore',

  // ── HUD ──
  'hud.gold': 'Gold',
  'hud.integrity': 'Island',
  'hud.tide': 'Tide',
  'hud.wave': 'Wave',
  'hud.shop': 'Coastal defenses',
  'hud.players': 'Defenders',
  'hud.preview': 'Next wave',
  'hud.preview_tsunami': 'The spray hides the sea — no forecast this wave.',
  'hud.call_wave': 'Call next wave',
  // Vote progress toast (0.21.0 fix — was raw `next_wave: 1/2` text): calm tally, no urgency.
  'vote.next_wave': 'Call the next wave — {votes}/{needed} captains agree.',
  'vote.surrender': 'Strike the colors — {votes}/{needed} captains agree.',
  // Call-early bonus (R3, Batch 0.20.0 — A-V4): a calm number on the EXISTING
  // button, computed live from `callEarlyBonus` (same formula/config as the
  // server). No urgency framing on the falling amount.
  'hud.call_wave_bonus': '+{n} gold',
  // Touch build parity (R5, Batch 0.20.0): first tap previews (ghost + range
  // ring), second tap on the same spot builds. Shown at most once per match.
  'hud.tap_to_build': 'Tap the same spot again to set it there.',
  'hud.build_phase': 'Build phase',
  'hud.wave_active': 'Wave in progress',
  'hud.upgrade': 'Upgrade',
  'hud.sell': 'Sell',
  'hud.level': 'Lvl',
  'hud.connecting': 'Signaling the harbor…',
  'hud.reconnecting': 'Lost the signal — re-hoisting the flag…',
  // Sound toggle (F3, Batch 0.14.0) — one label; aria-pressed/icon carry the state.
  'hud.sound_toggle': 'Toggle sound',

  // ── HUD: PvP send menu (Batch 0.5.0 — GAME_DESIGN §15) ──
  'hud.send_heading': 'Raid the enemy',
  'hud.send_income': 'Income',
  'hud.send_orders': 'Orders',
  'hud.send_lane_north': 'North',
  'hud.send_lane_south': 'South',
  'hud.surrender': 'Strike the colors',
  'hud.surrender_confirm': 'Confirm — the harbor falls',
  'send.send_small_warship.name': 'Small Warship',
  'send.send_small_warship.desc': 'A nimble broadside against their sea walls.',
  'send.send_large_warship.name': 'Large Warship',
  'send.send_large_warship.desc': 'Heavy oak that soaks up the coast guns.',
  'send.send_landing_spearmen.name': 'Landing: Spearmen',
  'send.send_landing_spearmen.desc': 'Three armored spearmen storm the land fortress.',
  'send.send_landing_plunderers.name': 'Landing: Plunderers',
  'send.send_landing_plunderers.desc': 'Two cutpurses drain their coffers — denial, never transfer.',
  'send.send_landing_siege.name': 'Landing: Siege',
  'send.send_landing_siege.desc': 'A siege catapult grinds their maze to splinters.',
  'send.rule_active': 'You can send only while the enemy wave is in progress.',
  'send.rule_cap': 'The dockmaster hires at most six crews a wave.',

  // ── HUD: PvP enemy status panel (Batch 0.5.0 — never gold, Pin P11) ──
  'hud.enemy_heading': 'Enemy harbor',
  'hud.enemy_integrity': 'Their island',
  'hud.enemy_income': 'Their income',
  'hud.enemy_incoming': 'Your raids',
  'hud.enemy_incoming_title': 'Crews you sent into their current wave.',

  // ── Defenses (shop) ──
  'defense.cliff_archers.name': 'Cliff Archers',
  'defense.cliff_archers.desc': 'Fast single shots, strong vs. light. Sea + land.',
  'defense.ballista_battery.name': 'Ballista Battery',
  'defense.ballista_battery.desc': 'Piercing bolts, strong vs. armored and ships. Sea + land.',
  'defense.catapult_emplacement.name': 'Catapult Emplacement',
  'defense.catapult_emplacement.desc': 'Area stone against packs. Land only.',
  'defense.pitch_kettle.name': 'Pitch Kettle',
  'defense.pitch_kettle.desc': 'Slows and burns at chokepoints. Land only.',
  'defense.harbor_barricade.name': 'Harbor Barricade',
  'defense.harbor_barricade.desc': 'Cheap maze wall — shapes the path.',
  'defense.tide_dam.name': 'Tide Dam',
  'defense.tide_dam.desc': 'Seals a shoal to redirect the current. Shoals only.',
  // ── Neue Küstenverteidigungen (Batch 0.16.0, WP1–WP4) ──
  'defense.trip_trap.name': 'Tripwire',
  'defense.trip_trap.desc': 'Flat snare on the path — one stumble per raider. Land only.',
  'defense.trip_trap.lore':
    'A hank of tarred rope pegged flat across the path. The first raider trips and eats sand; the rest step wide for a moment before they catch on.',
  'defense.anchor_winch.name': 'Anchor Winch',
  'defense.anchor_winch.desc': 'Hooks one raider and drags it back — once. Land only.',
  'defense.anchor_winch.lore':
    'A capstan and a boarding hook on a long chain. It reels a single raider back down the lane, then the line snaps — it never bites the same one twice.',
  'defense.flame_thrower.name': 'Flamethrower',
  'defense.flame_thrower.desc': 'Short-range gout of burning pitch — sets raiders alight. Land only.',
  'defense.flame_thrower.lore':
    'Bellows, a tar tank, and a nozzle. The shortest reach on the wall, but what it touches keeps burning long after the flame is gone.',
  'defense.pitch_kettle.name_l3': 'Lava Pit',
  'defense.pitch_kettle.becomes_l3': 'Upgrades into the Lava Pit — molten pitch from the fire-heart of the island.',
  'hud.tip.trip_trap':
    'Lies flat on the path — raiders trip over it. One stumble per raider, then they wise up for a moment.',
  'hud.tip.anchor_winch': 'One hook per raider — the line snaps after the first pull.',

  // ── Creeps (preview + tooltips) ──
  'creep.small_warship.name': 'Small Warship',
  'creep.large_warship.name': 'Large Warship',
  'creep.landing_boat.name': 'Landing Boat',
  'creep.stone_thrower.name': 'Stone Thrower',
  'creep.spearman.name': 'Spearman',
  'creep.plunderer.name': 'Plunderer',
  'creep.siege_catapult.name': 'Siege Catapult',
  'creep.flagship.name': 'The Black Fleet',

  // ── Lobby overlay ──
  'lobby.title': 'Harbor lobby',
  'lobby.waiting': 'Waiting for the crew…',
  'lobby.ready': 'Ready',
  'lobby.not_ready': 'Not ready',
  'lobby.start': 'Start the storm',
  'lobby.host_only': 'Only the host can start',
  'lobby.room_code': 'Room code',
  'lobby.host_badge': 'Host',
  'lobby.countdown': 'Storm inbound',
  'lobby.copy_code': 'Copy code',
  'lobby.copied': 'Copied',
  'lobby.share_hint': 'Share the code — your crew joins from the harbor page.',
  'lobby.qr_alt': 'QR code to join this room',
  'lobby.qr_hint': 'Scan to join',
  'lobby.slot_open': 'Open berth',
  // „First voyage" welcome card (F4, Batch 0.15.0 — role-correct per A-Q8: only the
  // action each role actually has; dismiss sets only the instance flag, never tw.hints_done).
  'lobby.welcome_host': "Welcome, captain — hit Start when you're ready. Share the room code to bring crew aboard.",
  'lobby.welcome_crew': 'Welcome aboard — mark yourself Ready; the captain sets sail.',

  // ── Result overlay ──
  'result.victory.title': 'The storm breaks — the harbor stands.',
  'result.defeat.title': 'The harbor is overrun — the tide takes it.',
  'result.abandoned.title': 'The harbor was abandoned',
  'result.crown': 'Harbor Master',
  'result.crown_note': 'All hail the Harbormaster — status, not power.',
  'result.rematch': 'Rematch',
  'result.stat.bounty': 'Bounty',
  'result.stat.kills': 'Kills',
  'result.stat.built': 'Built',
  'result.stat.stolen': 'Plundered',
  'result.reached': 'Reached wave',
  'result.duration': 'Duration',
  'result.difficulty': 'Difficulty',
  'result.back': 'Back to harbor',
  // Personal-best moments (F3, Batch 0.15.0 — coop_survival only; A-Q7 truth-conditional:
  // „New best!" ONLY when a prior best existed and was strictly beaten, else the quiet line.
  // Self-reference only — no compare/streak/expiry/CTA coupling).
  'result.best_new': '⚓ New best! Wave {n}',
  'result.best_line': 'Best ({diff}): Wave {n}',
  // Share moment (F5, Batch 0.15.0 — user-initiated copy; no reward, no nag, A-Q10).
  'result.share': 'Share result',
  'result.share_copied': 'Link copied',
  'result.share_failed': 'Copy failed',
  // Escalation invite (M1c, Batch 0.20.0 — A-V3, load-bearing): shown ONLY after a
  // real victory on the difficulty just played, and only up to Hurricane. Purely
  // descriptive — the copy is the difficulty name + its existing description, NEVER
  // a global-record claim („never beaten" is factually wrong) and never guilt copy
  // about the lower rung. Opt-in: a subordinate text link, no second button.
  'result.escalate': 'If you want rougher weather: {name} — {desc}',
  'result.escalate_link': 'Choose that course',

  // ── Result outcome badge (Batch 0.5.0, mode-übergreifend — GAME_DESIGN §15) ──
  'result.outcome.victory': 'Victory',
  'result.outcome.defeat': 'Defeat',
  'result.outcome.abandoned': 'Abandoned',
  'result.outcome.draw': 'Draw',

  // ── Result: PvP viewer-relative flavor headlines (Batch 0.5.0) ──
  'result.pvp.victory.title': 'Their harbor is ash — the plunder is yours.',
  'result.pvp.defeat.title': 'Your walls broke first — they take the spoils.',
  'result.pvp.draw.title': 'Both harbors held — the sea calls it even.',
  'result.pvp.abandoned.title': 'The duel dissolved into the fog.',

  // ── Toasts / banners ──
  'toast.plunder': "A plunderer slipped through — {n} gold from every defender's coffers.",
  // Wave pay (R2, Batch 0.20.0 — A-V5): the counterpart to the plunder LOSS toast
  // above, same register. Display only (the server's gold_changed stays authoritative),
  // never audio on economy events, no escalating celebration chain.
  'toast.sold_paid': 'The wave is paid out — {n} gold for every defender.',
  'toast.breach': 'A broadside slams the harbor — {n} integrity torn away.',
  'toast.main_house_hit': 'Raiders reached the main house — {n} integrity lost.',
  'toast.structure_destroyed': 'A siege catapult smashed a barricade — rebuild the breach.',
  'toast.tsunami': 'The tide is wrong. The old salts bar their doors — TSUNAMI INBOUND.',
  // Batch 0.5.0 — PvP + anti-stall (GAME_DESIGN §15 / §2)
  'toast.incoming_send': 'Enemy crews are inbound — brace your walls.',
  'toast.creep_stalled': 'Penned in too long — the raiders storm the walls. The tide waits for no one.',
  'toast.pvp_downgraded': 'PvP is taking on water — we raised a co-op harbor instead.',
  'toast.send_launched': 'Crews away — your income is now {n}.',
  // Batch 0.12.0 — Tide Straits (§16)
  'toast.straits_downgraded': 'The straits are closed — we raised a co-op harbor instead.',
  'toast.dam_inert': 'The dam holds — but the fleet still finds an open strait. Wall off another channel to force the detour.',
  'tide.flood': 'Flood',
  'tide.ebb': 'Ebb',
  'tide.banner_flood': 'The tide floods east — the current drives the fleets inland.',
  'tide.banner_ebb': 'The tide ebbs west — the current drags the fleets seaward.',

  // ── First-match hints (F4, Batch 0.14.0 — coop only, players only; A5:
  //    mechanic-existence only, no comparative/imperative strategy, ownership-neutral) ──
  'hint.dismiss': 'Dismiss',
  'hint.card1': 'Pick a coastal defense below and set it on the shore — the fleet comes in from the sea.',
  'hint.card2': 'Towers fire on their own. Every sunken ship pays the whole crew — upgrades keep you ahead of the storm.',
  'hint.card3': 'Walls and channels reroute landing parties — build detours to buy time.',
  // First-wave-survived toast (F4, Batch 0.15.0 — truth-conditional on the real
  // wave-1-cleared event, once per match, nominal tone, never imperative; A-Q8c).
  'hint.first_wave': 'The harbor holds! Twelve more storms are on their way.',

  // ── Action rejections (reason code → line) ──
  'reject.mode_restricted': 'That defense does not belong in these straits.',
  'reject.full_block': 'The war council refuses: every cove keeps one landing path open.',
  'reject.out_of_bounds': 'You cannot build beyond the bay.',
  'reject.reserved_cell': 'That ground is reserved — landing zones and the main house stay clear.',
  'reject.occupied_defense': 'A defense already stands there.',
  'reject.occupied_creep': 'A landing party stands there — clear the ground first.',
  'reject.insufficient_gold': 'Not enough gold in the coffers.',
  'reject.not_owner': 'Only the builder may sell or upgrade this post.',
  'reject.max_level': 'This post is already at full strength.',
  'reject.not_found': 'That post is gone.',
  'reject.not_building_phase': 'You can only call the wave during a build pause.',
  'reject.not_connected': 'You are not connected.',
  'reject.not_running': 'The match is not running.',
  'reject.no_such_player': 'You are not seated in this match.',
  'reject.not_implemented': 'Not available yet.',
  'reject.not_initialized': 'This harbor is not ready yet.',
  'reject.match_in_progress': 'The battle has already begun — you cannot join mid-storm.',
  'reject.room_full': 'This harbor is full (4 defenders).',
  'reject.bad_message': 'The signal was garbled.',
  'reject.binary_unsupported': 'Unsupported message format.',
  // Batch 0.5.0 — PvP send rejections (GAME_DESIGN §15)
  'reject.not_in_pvp': 'You cannot call the wave in a duel — the storm clock is shared.',
  'reject.unknown_send': "No such crew on the dockmaster's roster.",
  'reject.bad_count': 'One send is one crew — no more.',
  'reject.bad_lane': 'Pick the north or south channel.',
  'reject.send_cap_reached': 'The dockmaster hires at most six crews a wave.',
  'reject.target_not_active': 'You can send only while the enemy wave is in progress.',

  // ── Wave banners: title (GAME_DESIGN §6 theme) + Chronicle-register flavor
  //    (STORY-LINE §6 tone — dry, "epic with a wink", ≤ 90 chars, no magic) ──
  'wave.1.title': 'Scouts on the horizon',
  'wave.1.flavor': 'Scouts first, always. They count your walls before they wet their boots.',
  'wave.2.title': 'The sloops smell gold',
  'wave.2.flavor': 'The sloops smell gold on the wind — and sloops are rarely wrong.',
  'wave.3.title': 'Spears in the surf',
  'wave.3.flavor': 'The beach belongs to whoever still stands when the surf pulls back.',
  'wave.4.title': 'They come for your coffers',
  'wave.4.flavor': "They're not here for the island. They're here for the coffers.",
  'wave.5.title': 'Heavy oak on the tide',
  'wave.5.flavor': 'Heavy oak on the tide — slow, ponderous, and stubborn about sinking.',
  'wave.6.title': 'A swarm with slings',
  'wave.6.flavor': "A swarm with slings. No single stone matters. That's the trouble with swarms.",
  'wave.7.title': 'The sea rises against us all',
  'wave.7.flavor': 'The tide is wrong. The sea rises against us all — both channels at once.',
  'wave.8.title': 'Rolling thunder ashore',
  'wave.8.flavor': 'Rolling thunder comes ashore. A flying boulder reads no peace treaties.',
  'wave.9.title': 'The line of oak',
  'wave.9.flavor': 'A line of oak on the water. Break the escort, or it breaks your beach.',
  'wave.10.title': 'Beachhead!',
  'wave.10.flavor': "Beachhead. Five hulls hit the sand at once — the surf won't hold them.",
  'wave.11.title': 'Cutpurses in the smoke',
  'wave.11.flavor': 'Cutpurses in the smoke. They take the gold and say their goodbyes loudly.',
  'wave.12.title': 'The fleet gathers',
  'wave.12.flavor': 'The whole fleet gathers offshore. This is the knock before the door falls.',
  'wave.13.title': 'The Black Fleet',
  'wave.13.flavor': 'Black sails, red decks — the Black Fleet itself. Sink it, or write no more logs.',

  // ── Damage-type + target badges (shop cards) ──
  'damage.arrow': 'Arrow',
  'damage.bolt': 'Bolt',
  'damage.stone': 'Stone',
  'damage.pitch': 'Pitch',
  'target.sea_land': 'Sea + Land',
  'target.land': 'Land',
  'target.none': 'Wall',

  // ── Defense lore tooltips (GAME_DESIGN §3 tone; catapult carries ADR-005) ──
  'defense.cliff_archers.lore': 'Cheap eyes and quick bows on the cliffs — they thin the light, not the armored.',
  'defense.ballista_battery.lore': 'A bolt that punches oak and plate alike. Slow to load, hard to shrug off.',
  'defense.catapult_emplacement.lore': 'Lobs stone into packs on the sand. Too imprecise against rolling waves — land targets only.',
  'defense.pitch_kettle.lore': 'Boiling pitch for the chokepoints — it slows the charge and keeps on burning.',
  'defense.harbor_barricade.lore': 'A cheap wall of stakes. It shapes the maze; the sea has to walk the long way.',
  'defense.tide_dam.lore': 'Rubble and timber choke a shoal. The current bends around it — and so must the fleet.',

  // ── Creep lore tooltips (preview chips; STORY-LINE §3/§4 tone) ──
  'creep.small_warship.lore': 'An agile reef patrol — it fires a broadside at the quay, then wheels away.',
  'creep.large_warship.lore': 'A floating oak fortress, slow and ponderous, with a heavy broadside to match.',
  'creep.landing_boat.lore': 'A bulging barge. Sink it at sea, or its troops walk your beach.',
  'creep.stone_thrower.lore': 'Slings and desperation — a hasty militia that still dents a main house.',
  'creep.spearman.lore': 'The armored shield wall of the sand. Arrows glance; bolts do not.',
  'creep.plunderer.lore': 'Fast and light — he takes gold, not lives, and says his goodbyes loudly.',
  'creep.siege_catapult.lore': 'A rolling clockwork of destruction. It grinds barricades to splinters.',
  'creep.flagship.lore': 'The Black Fleet itself — black sails, red decks, a broadside that ends logs.',

  // ── Preview markers ──
  'hud.preview_lands': 'lands',
  'hud.crown_title': 'Top bounty',

  // ── Reconnect overlay ──
  'reconnect.title': 'The line snapped — casting again…',
  'reconnect.done': 'Line restored in {ms} ms.',

  // ════════════════════════════════════════════════════════════════════════
  // Meta-Surface (Batch 0.4.0) — SEO, changelog, feedback, lobby, cross-promo
  // ════════════════════════════════════════════════════════════════════════

  // ── Footer navigation + page titles + meta descriptions ──
  'nav.changelog': "Captain's log",
  'nav.feedback': 'Feedback',
  'meta.title.home': 'TideWars — Real-time Co-op Tower Defense',
  'meta.title.play': 'Choose your course — TideWars',
  'meta.title.changelog': "Captain's log — TideWars",
  'meta.title.feedback': 'Feedback — TideWars',
  'meta.title.room_not_found': 'That cove is empty — TideWars',
  'meta.title.match': 'TideWars — Harbor Defense',
  'meta.desc.home':
    'A free real-time co-op tower defense in the Kampfinsel universe. Jump straight into short harbor-defense matches — no account, no download, no persistent world to tend.',
  'meta.desc.changelog':
    "Every storm weathered and every plank mended in TideWars — the running captain's log, newest first.",
  'meta.desc.feedback':
    'Share an idea, a bug, or a balance note for TideWars — player signal that steers the design.',

  // ── Share moment (F5, Batch 0.15.0 — /s battle report, noindex). A-Q9: neutral,
  //    self-reported, GET-only CTA; NEVER challenge/compare copy, NEVER „survived" on
  //    a defeat (outcome-correct statement). Title = statement + brand. ──
  'meta.title.share': '{stmt} — TideWars',
  'share.description':
    'A shared harbor-defense result from TideWars — a free real-time co-op tower defense. No account, no download.',
  'share.eyebrow': 'Battle report',
  'share.stmt_victory': 'All 13 waves survived on {diff}',
  'share.stmt_defeat': 'Reached wave {n} of 13 on {diff}',
  'share.self_reported': 'A self-reported harbor-defense result.',
  'share.cta': 'Raise your own harbor',

  // ── Landing: hero + create/join extras ──
  'landing.private_label': 'Make this harbor private (hide it from the list)',
  // A3 (Batch 0.6.0): the consent copy names the new watch aspect in one breath —
  // „public" now means listed AND live-watchable once the storm begins.
  'landing.public_wirkort':
    'Your harbor appears in the list on the home page — anyone can drop anchor to join, and watch it live once the storm begins.',

  // ── Public lobby browser (FOMO-free, honest) ──
  'lobby.list_heading': 'Open harbors',
  'lobby.list_empty': 'No crews at sea yet — raise the first harbor and share the code.',
  'lobby.list_join': 'Join',
  'lobby.kick': 'Remove',

  // ── Live spectating (Batch 0.6.0 — honest counts, no scarcity, Pin P2) ──
  'live.heading': 'Battles underway',
  'live.watch': 'Watch',
  'live.wave_progress': 'Wave {n}/13',
  'live.spectator_count': '👁 {n}',
  'spectate.live_badge': 'LIVE',
  'spectate.nudge_start': 'Raise your own harbor',
  'spectate.ended': 'The battle is over — the tide has turned.',
  'spectate.unavailable': 'No live battle to watch here — it may have ended or set sail elsewhere.',

  // ── Kampfinsel cross-promo (nominal, no imperative — Gate P4/P5) ──
  'crosspromo.eyebrow': 'From the same universe',
  'crosspromo.copy':
    'Kampfinsel is a persistent island world of slow, long-haul voyages. TideWars is its own game — short, self-contained matches with their own progress, and no persistent world to keep.',
  'crosspromo.link': 'Kampfinsel — the persistent island world',
  'crosspromo.banner_alt': 'Kampfinsel — persistent island world from the same universe.',

  // ── Changelog (chrome i18n; entry content EN-only, Gate-O-b/O-c) ──
  'changelog.heading': "Captain's log",
  'changelog.intro': "Every storm we've weathered and every plank we've mended — newest first.",
  'changelog.entries_en_notice': 'Entries are currently in English.',
  'changelog.empty': 'The log is dry — no entries yet.',
  'changelog.cat.added': 'Added',
  'changelog.cat.changed': 'Changed',
  'changelog.cat.fixed': 'Fixed',
  'changelog.cat.removed': 'Removed',

  // ── Feedback ──
  'feedback.heading': 'Feedback',
  'feedback.intro': 'Tell us what would make the harbor better.',
  'feedback.field_type': 'Type',
  'feedback.field_title': 'Summary',
  'feedback.field_details': 'Details (optional)',
  'feedback.title_placeholder': 'One line — what is it?',
  'feedback.details_placeholder': 'Anything that helps us understand it.',
  'feedback.submit': 'Send feedback',
  'feedback.list_heading': 'What the crew is saying',
  'feedback.expectation': 'This is signal for the design decision — not a vote that decides it.',
  'feedback.empty': 'No feedback yet — be the first to log a note.',
  'feedback.type.idea': 'Idea',
  'feedback.type.bug': 'Bug',
  'feedback.type.balance': 'Balance',
  'feedback.upvote': 'Agree',
  'feedback.downvote': 'Disagree',
  'feedback.vote_locked': 'Voting opens after your first match.',
  'feedback.notice.submitted': 'Logged — thanks for the signal.',
  'feedback.notice.rate_limited': 'You have logged plenty today — try again tomorrow.',
  'feedback.notice.invalid': 'That note could not be logged — check the fields and try again.',
  'feedback.notice.voted': 'Your vote is in.',
  'feedback.notice.vote_locked': 'Voting opens after your first match.',
  'feedback.notice.vote_rate_limited': 'That is a lot of votes for one day — try again tomorrow.',
  // Turnstile reject notices (Batch 0.11.0, A-TS4) — the re-render keeps the typed
  // fields, so both lines reassure „your note is still here" (plain tone).
  'feedback.notice.captcha_invalid': 'The security check did not finish in time — please send it again. Your note is still here.',
  'feedback.notice.captcha_missing':
    'The security check could not load (a browser extension may be blocking it). Your note is still here.',

  // ── Relative time (feedback list) ──
  'time.just_now': 'just now',
  'time.minutes': '{n} min ago',
  'time.hours': '{n} h ago',
  'time.days': '{n} d ago',

  // ── Match reject additions (host kick, Batch 0.4.0) ──
  'reject.kicked': 'The harbor master cleared your berth — you have left this harbor.',
  'reject.join_paused': 'The harbor master just cleared a berth — try again in a moment.',

  // ════════════════════════════════════════════════════════════════════════
  // Account / Auth v1 (Batch 0.9.0) — "Claim your captain's name" (noindex)
  // ════════════════════════════════════════════════════════════════════════
  'nav.account': 'Captain',
  'meta.title.account': 'Your captain — TideWars',
  'account.heading': "Captain's quarters",
  'account.intro': 'A name at the helm the crew will remember — no email, no download.',
  'account.login.heading': 'Return to the helm',
  'account.login.button': 'Sign in',
  'account.register.heading': "Claim your captain's name",
  'account.register.button': 'Claim it',
  'account.register.nickname_rule': '3–24 characters: letters, digits, - and _ — no spaces.',
  'account.register.disclosure':
    'No email needed — and none collected. There is no password reset yet, so keep it safe.',
  'account.field.nickname': "Captain's name",
  'account.field.password': 'Password',
  'account.signed_in_as': 'At the helm:',
  'account.member_since': 'Sailing since',
  'account.logout': 'Sign out',
  'account.err.invalid_nickname': "That name won't do — 3–24 letters, digits, - or _, and nothing reserved.",
  'account.err.nickname_taken': 'Another captain already sails under that name.',
  'account.err.weak_password': 'Your password needs at least 8 characters (and at most 72 bytes).',
  'account.err.bad_credentials': "That name and password don't match our logs.",
  'account.err.rate_limited': 'Too many attempts from your network — try again later.',
  'account.msg.registered': 'Welcome aboard, captain — your name is claimed.',

  // ════════════════════════════════════════════════════════════════════════
  // Fleet Intelligence (Zahlen-Transparenz im Match) — Shop / Tower-Panel /
  // Wellen-Vorschau / PvP-Send. Nüchtern-knappe Stat-Labels; Multiplikatoren
  // locale-neutral als ×N.NN, max. 2 Dezimalstellen (Zahlen setzt der Client).
  // ════════════════════════════════════════════════════════════════════════
  'stat.dps': 'DPS',
  'stat.range': 'Range',
  'stat.cells': 'cells',
  'stat.cells_per_s': 'cells/s',
  'stat.hp': 'HP',
  'stat.damage_shot': 'Dmg/shot',
  'stat.fire_rate': 'Fire rate',
  'stat.pierce': 'Pierce',
  'stat.aoe': 'Area radius',
  'stat.slow': 'Slow',
  'stat.burn': 'Burn',
  'stat.structure_hp': 'Structure HP',
  'stat.contact': 'Contact/s',
  'stat.vs_light': 'vs. light',
  'stat.vs_armored': 'vs. armored',
  // Qualitative counter wording (R4, Batch 0.20.0 — A-V6a): derived at RUNTIME from
  // the live damage matrix (thresholds over the multipliers), never hardcoded per
  // tower — a KV `config:balance` patch must not turn the shop card into a liar.
  'stat.counter_strong': 'Strong',
  'stat.counter_weak': 'Weak',
  'stat.counter_even': 'Even',
  'stat.payload': 'Cargo',
  'stat.broadside': 'Broadside',
  'stat.breach': 'Breach',
  'stat.steals_gold': 'Steals gold',
  'armor.light': 'Light armor',
  'armor.armored': 'Heavy armor',
  'tower.upgrade_preview': '→ L{lvl}: {dmg} dmg · {rate}/s',
  'tower.upgrade_preview_hp': '→ L{lvl}: {hp} HP',
  'tower.built_by': 'Built by {name} — only the builder may upgrade or sell this post.',
  'result.stat.damage': 'Damage',
  'hud.send_payback': 'amortized in ~{n} waves',
  'send.payback_wirkort': 'Income pays out only at your own wave-ends, through wave 13.',

  // ════════════════════════════════════════════════════════════════════════
  // Captain's Orders + Zielwahl (Release 0.21.0 „Next Level", Gate-G) — pre-
  // gunpowder only (fire pots, burning tar, war drums — never cannons/powder).
  // Numbers are interpolated from the live config ({n}/{dmg}/… filled by the
  // client), never hardcoded. A-O8: charges are a calm state display — no
  // countdown pressure, no "wasted" framing; locked orders say "from wave N"
  // without urging. Hints: existence form only (F4 criteria).
  // ════════════════════════════════════════════════════════════════════════
  'order.volley.name': 'Fire Pot Volley',
  'order.tar_trap.name': 'Tar Trap',
  'order.rally.name': 'Beat to Quarters',
  'order.volley.short': 'Volley',
  'order.tar_trap.short': 'Tar',
  'order.rally.short': 'Drum',
  'order.volley.tip':
    'Fire pots arc from the harbor walls and burst after {delay}s: {dmg} damage to every raider in a {r}-cell ring, then {dps} burn per second for {dur}s.',
  'order.tar_trap.tip':
    'Burning tar on open ground, armed after {arm}s: the first raider to step in sets it off — {dmg} damage in a {r}-cell ring plus {dps} burn per second for {dur}s; up to {max} can lie at once.',
  'order.rally.tip': 'The war drum rolls: every tower within {r} cells fires ×{mult} as fast for {dur}s.',
  'orders.bar_label': "Captain's orders",
  'orders.unlock_short': 'W{n}',
  // A-O10 b (Tsunami lock): ONE sentence at the place of effect — the spray, not powder.
  'orders.storm_locked': 'The tide douses the fire pots, washes out the tar and drowns the drums — no orders this wave.',
  'orders.storm_warning':
    'The coming tide will douse the fire pots, wash out the tar and drown the drums — no orders in that wave.',
  'orders.aim.mouse': '{name}: click a target — Esc or right-click cancels.',
  'orders.aim.touch': '{name}: tap a target, then tap it again to confirm.',
  'orders.tap_confirm': 'Tap the same spot again to give the order.',
  'orders.reason.locked': '{name} unlocks at wave {n}.',
  'orders.reason.phase.volley': 'Fire pots fly only while a wave is underway.',
  'orders.reason.phase.tar_trap': 'Tar can be laid only while the match is running.',
  'orders.reason.phase.rally': 'The war drum sounds only while a wave is underway.',
  'orders.reason.spent': '{name} is used for this wave — the next wave brings a fresh charge.',
  'orders.reason.rearming': 'The crew is still re-arming — {name} is ready again in a moment.',
  // A-O6 a: names the cap AND how a slot frees up.
  'orders.reason.trap_limit':
    'All {max} of your tar traps are laid — remove one (click it) or let one spring to lay another.',
  'orders.reason.target.volley': 'Fire pots can only land on the bay or its sea lanes.',
  'orders.reason.target.tar_trap': 'Tar only holds on open ground the raiders can walk.',
  'orders.reason.target.rally': 'The war drum needs at least one tower within earshot.',
  'orders.reason.reserved': 'Tar stays off the landing zones and the main house.',
  'orders.reason.occupied_defense': 'A defense stands there — tar needs open ground.',
  'orders.reason.occupied_trap': 'A tar trap already lies there.',
  'orders.reason.mode': "In the Tide Straits, the captain's orders stay in port.",
  // Targeting (A2) — only First/Last/Strongest are exposed (A-O11); tooltips in
  // existence form without comparatives, wording per A-O13.
  'targeting.label': 'Aim',
  'targeting.first': 'First',
  'targeting.last': 'Last',
  'targeting.highest_hp': 'Strongest',
  'targeting.first.tip': 'First — aims at the raider closest to breaking through; coastal posts take ships first.',
  'targeting.last.tip': 'Last — aims at the raider furthest from breaking through.',
  'targeting.highest_hp.tip':
    'Strongest — aims at the raider with the most hull left; on the coast that is often the landing boat, not the warship.',
  'targeting.read_only': "Only the builder may set this post's aim.",
  'targeting.none': 'This post has no target to choose.',
  'targeting.mode': 'In the Tide Straits every post aims at the foremost ship.',
  // Tar-trap mini panel (A-O6 a: the owner removes a laid trap for free).
  'trap.arming': 'Arming…',
  'trap.armed': 'Armed — the first raider to step in sets it off.',
  'trap.dormant': 'Dormant — the tide has doused it until the next wave.',
  'trap.remove': 'Remove (free)',
  'trap.laid_by': 'Laid by {name}.',
  'trap.gone': 'That tar trap is already gone.',
  'trap.not_owner': 'Only the captain who laid this trap can remove it.',
  // "First voyage" of the orders (A-O8 b): one hint per unlock, once per device.
  'hint.order.volley':
    "Captain's order ready: the Fire Pot Volley — pick it in the order bar, then a spot; the pots burst over everything in the ring.",
  'hint.order.tar_trap':
    'New order: the Tar Trap — laid on open ground, it catches fire under the first raider and burns the pack around it. It lies in wait until it springs.',
  'hint.order.rally':
    'New order: Beat to Quarters — the war drum makes every tower in earshot fire faster for a few seconds.',
  'lobby.straits_orders': "In the Tide Straits the captain's orders stay in port — dams and towers carry the day.",
  // Server reject codes without action context (the client usually explains with context).
  'reject.order_locked': 'That order unlocks in a later wave.',
  'reject.storm_locked': 'The tide douses the fire pots, washes out the tar and drowns the drums — no orders this wave.',
  'reject.policy_unavailable': 'That aim is not available for this post.',
  'reject.not_wave_phase': 'That order works only while a wave is underway.',
  'reject.no_charges': 'That order is used for this wave — the next wave brings a fresh charge.',
  'reject.order_rearming': 'The crew is still re-arming — give them a moment.',
  'reject.invalid_target': 'That order cannot land there.',
  'reject.trap_limit': 'All your tar traps are laid — remove one (click it) or let one spring to lay another.',
  'reject.occupied_trap': 'A tar trap lies there — whoever laid it can remove it for free.',
};

const de: Dictionary = {
  // ── Landing ──
  'landing.tagline': 'Echtzeit-Tower-Defense auf offener See — verteidige deine Insel, plündere ihre.',
  'landing.status': 'Fundament-Phase — die Flut steigt.',
  'landing.play': 'Spielen',
  // A-L2/A-Q3 (Batch 0.15.0): der primäre CTA ist nominal — keine Dringlichkeit/Scarcity/Countdown.
  // Quick Play (F1): ein Klick gründet einen Standard-Koop-Hafen; „Kurs wählen" ist
  // der subordinierte Text-Link zur vollen /play-Konfiguration.
  'landing.play_now': 'Jetzt spielen',
  'landing.play_now_sub': 'Ein Klick — dein Hafen erhebt sich. Ohne Account, ohne Download.',
  'landing.play_choose': 'Kurs wählen — Modus, Stärke, privat',
  'landing.difficulty_label': 'Schwierigkeit',
  'landing.join_heading': 'Mit Raum-Code beitreten',
  'landing.join_sub': 'Einen Code von Freunden? Hier ankern.',
  'landing.join_code_label': 'Raum-Code',
  'landing.join_button': 'Beitreten',
  // Runden-Ticker (Batch 0.8.0, A-T5) — s. EN-Kommentar (Bestandsform, kein CTA).
  'landing.rounds_ticker': '{n} Schlachten auf hoher See geschlagen.',

  // ── Logbuch des Käpt'ns (F5, Batch 0.14.0 — gerätelokal, Bestandsform, A7) ──
  'landing.log_heading': 'Dein Logbuch',
  'landing.log_played': 'Schlachten geschlagen',
  'landing.log_won': 'gewonnen',
  'landing.log_bestwave': 'Beste Welle:',

  // ── /play — „Kurs wählen"-Schritt (Batch 0.8.0) ──
  'play.heading': 'Kurs wählen',
  'play.sub': 'Wähle Modus und Wetterlage und gründe dann deinen Hafen.',
  'play.start_button': 'Hafen gründen',
  'play.back': 'Zurück an Land',
  // Turnstile-Reject-Notices (Batch 0.11.0, A-TS4) — nüchtern, kein Bot-Vorwurf.
  'play.notice.captcha_invalid': 'Der Sicherheits-Check wurde nicht rechtzeitig fertig — gründe den Hafen bitte erneut.',
  'play.notice.captcha_missing':
    'Der Sicherheits-Check konnte nicht laden (womöglich blockiert eine Browser-Erweiterung ihn). Der Beitritt per Raumcode bleibt offen.',

  // ── Mode selection (Batch 0.5.0 — GAME_DESIGN §15) ──
  'landing.mode_label': 'Modus',
  'mode.coop_survival.name': 'Hafenverteidigung',
  'mode.coop_survival.desc': 'Verbündet euch und haltet den Hafen gegen den Sturm — bis zu vier Verteidiger.',
  'mode.coop_survival.badge': 'Koop',
  'mode.pvp_duel.name': 'Plünderkrieg',
  'mode.pvp_duel.desc': 'Zwei Spiegel-Inseln, ein Duell — überdauere den Rivalen und plündere seine Küste.',
  'mode.pvp_duel.badge': '1v1',
  'mode.tide_straits.name': 'Gezeitenstraßen',
  'mode.tide_straits.desc': 'Versiegle die Untiefen und würge die Flotten, wenn die Tide kippt — bis zu vier Wächter.',
  'mode.tide_straits.badge': 'Straße',

  // ── Difficulty (Lobby + Landing) ──
  'difficulty.light.name': 'Leichte Brise',
  'difficulty.light.desc': 'Sanftere Flotten, robustere Insel — eine ruhige erste Fahrt.',
  'difficulty.stiff.name': 'Steife Brise',
  'difficulty.stiff.desc': 'Die Referenz — ausgewogene Angriffe, normaler Rumpf.',
  'difficulty.hurricane.name': 'Orkan',
  'difficulty.hurricane.desc': 'Unerbittliche Wellen, spröde Insel — für erprobte Hafenmeister.',

  // ── Room-not-found ──
  'room.notfound.title': 'Diese Bucht ist leer',
  'room.notfound.body': 'Kein Hafen antwortet auf diesen Code — er ist wohl davongesegelt oder existierte nie.',
  'room.notfound.back': 'Zurück an Land',

  // ── HUD ──
  'hud.gold': 'Gold',
  'hud.integrity': 'Insel',
  'hud.tide': 'Tide',
  'hud.wave': 'Welle',
  'hud.shop': 'Küstenverteidigung',
  'hud.players': 'Verteidiger',
  'hud.preview': 'Nächste Welle',
  'hud.preview_tsunami': 'Die Gischt verschluckt die See — diese Welle kommt ohne Vorwarnung.',
  'hud.call_wave': 'Nächste Welle rufen',
  'vote.next_wave': 'Nächste Welle rufen — {votes}/{needed} Kapitäne sind dafür.',
  'vote.surrender': 'Flagge streichen — {votes}/{needed} Kapitäne sind dafür.',
  // Call-early-Bonus (R3, Batch 0.20.0 — A-V4): ruhige Zahl am BESTEHENDEN Button,
  // live aus `callEarlyBonus` (dieselbe Formel/Config wie der Server).
  'hud.call_wave_bonus': '+{n} Gold',
  // Touch-Bau-Parität (R5, Batch 0.20.0): erster Tap zeigt Ghost + Reichweitenring,
  // zweiter Tap auf dieselbe Stelle baut. Höchstens einmal pro Match.
  'hud.tap_to_build': 'Tippe dieselbe Stelle erneut an, um dort zu bauen.',
  'hud.build_phase': 'Bauphase',
  'hud.wave_active': 'Welle läuft',
  'hud.upgrade': 'Ausbauen',
  'hud.sell': 'Verkaufen',
  'hud.level': 'Stufe',
  'hud.connecting': 'Signal an den Hafen…',
  'hud.reconnecting': 'Signal verloren — die Flagge wieder hissen…',
  // Sound-Toggle (F3, Batch 0.14.0) — ein Label; aria-pressed/Icon tragen den Zustand.
  'hud.sound_toggle': 'Ton umschalten',

  // ── HUD: PvP send menu (Batch 0.5.0 — GAME_DESIGN §15) ──
  'hud.send_heading': 'Feindinsel plündern',
  'hud.send_income': 'Einkommen',
  'hud.send_orders': 'Aufträge',
  'hud.send_lane_north': 'Nord',
  'hud.send_lane_south': 'Süd',
  'hud.surrender': 'Die Flagge streichen',
  'hud.surrender_confirm': 'Bestätigen — der Hafen fällt',
  'send.send_small_warship.name': 'Kleines Kriegsschiff',
  'send.send_small_warship.desc': 'Eine flinke Breitseite gegen ihre Seemauern.',
  'send.send_large_warship.name': 'Großes Kriegsschiff',
  'send.send_large_warship.desc': 'Schwere Eiche, die die Küstengeschütze schluckt.',
  'send.send_landing_spearmen.name': 'Landung: Speerträger',
  'send.send_landing_spearmen.desc': 'Drei gepanzerte Speerträger stürmen die Landfestung.',
  'send.send_landing_plunderers.name': 'Landung: Plünderer',
  'send.send_landing_plunderers.desc': 'Zwei Beutelschneider leeren ihre Kassen — Verwehrung, nie Übertrag.',
  'send.send_landing_siege.name': 'Landung: Belagerung',
  'send.send_landing_siege.desc': 'Ein Belagerungskatapult mahlt ihr Labyrinth zu Splittern.',
  'send.rule_active': 'Senden geht nur, während die Welle des Gegners läuft.',
  'send.rule_cap': 'Der Hafenmeister heuert höchstens sechs Trupps pro Welle an.',

  // ── HUD: PvP enemy status panel (Batch 0.5.0 — niemals Gold, Pin P11) ──
  'hud.enemy_heading': 'Feindhafen',
  'hud.enemy_integrity': 'Ihre Insel',
  'hud.enemy_income': 'Ihr Einkommen',
  'hud.enemy_incoming': 'Deine Angriffe',
  'hud.enemy_incoming_title': 'Trupps, die du in ihre aktuelle Welle geschickt hast.',

  // ── Defenses (shop) ──
  'defense.cliff_archers.name': 'Klippenschützen',
  'defense.cliff_archers.desc': 'Schnelle Einzelschüsse, stark gegen Leichtes. See + Land.',
  'defense.ballista_battery.name': 'Balliste-Batterie',
  'defense.ballista_battery.desc': 'Durchschlagende Bolzen, stark gegen Panzer und Schiffe. See + Land.',
  'defense.catapult_emplacement.name': 'Katapult-Stellung',
  'defense.catapult_emplacement.desc': 'Flächenstein gegen Rudel. Nur Land.',
  'defense.pitch_kettle.name': 'Pechkessel',
  'defense.pitch_kettle.desc': 'Verlangsamt und brennt an Engstellen. Nur Land.',
  'defense.harbor_barricade.name': 'Hafenbarrikade',
  'defense.harbor_barricade.desc': 'Billige Labyrinth-Mauer — formt den Pfad.',
  'defense.tide_dam.name': 'Gezeitendamm',
  'defense.tide_dam.desc': 'Versiegelt eine Untiefe und lenkt die Strömung um. Nur auf Untiefen.',
  // ── Neue Küstenverteidigungen (Batch 0.16.0, WP1–WP4) ──
  'defense.trip_trap.name': 'Stolperfalle',
  'defense.trip_trap.desc': 'Flache Schlinge auf dem Pfad — ein Stolpern je Räuber. Nur Land.',
  'defense.trip_trap.lore':
    'Ein Bündel geteerten Taus, flach über den Pfad gepflockt. Der erste Räuber stolpert und frisst Sand; die anderen weichen einen Moment aus, ehe sie es merken.',
  'defense.anchor_winch.name': 'Ankerwinde',
  'defense.anchor_winch.desc': 'Hakt einen Räuber und zerrt ihn zurück — einmal. Nur Land.',
  'defense.anchor_winch.lore':
    'Ein Spill und ein Enterhaken an langer Kette. Sie holt einen einzelnen Räuber die Gasse zurück, dann reißt die Leine — denselben hakt sie nie ein zweites Mal.',
  'defense.flame_thrower.name': 'Flammenwerfer',
  'defense.flame_thrower.desc': 'Kurzer Schwall brennenden Pechs — setzt Räuber in Brand. Nur Land.',
  'defense.flame_thrower.lore':
    'Blasebalg, Teertank und Düse. Die kürzeste Reichweite auf der Mauer, doch was sie trifft, brennt lange weiter, wenn die Flamme längst erloschen ist.',
  'defense.pitch_kettle.name_l3': 'Lava-Grube',
  'defense.pitch_kettle.becomes_l3': 'Wird zur Lava-Grube — glühendes Pech aus dem Feuer-Herz der Insel.',
  'hud.tip.trip_trap':
    'Liegt flach auf dem Pfad — Räuber stolpern darüber. Ein Stolpern je Räuber, dann sind sie für einen Moment gewarnt.',
  'hud.tip.anchor_winch': 'Ein Haken je Räuber — die Leine reißt nach dem ersten Zug.',

  // ── Creeps (preview + tooltips) ──
  'creep.small_warship.name': 'Kleines Kriegsschiff',
  'creep.large_warship.name': 'Großes Kriegsschiff',
  'creep.landing_boat.name': 'Landungsboot',
  'creep.stone_thrower.name': 'Steinschleuderer',
  'creep.spearman.name': 'Speerträger',
  'creep.plunderer.name': 'Plünderer',
  'creep.siege_catapult.name': 'Belagerungskatapult',
  'creep.flagship.name': 'Die Schwarze Flotte',

  // ── Lobby overlay ──
  'lobby.title': 'Hafen-Lobby',
  'lobby.waiting': 'Warte auf die Crew…',
  'lobby.ready': 'Bereit',
  'lobby.not_ready': 'Nicht bereit',
  'lobby.start': 'Den Sturm entfesseln',
  'lobby.host_only': 'Nur der Host kann starten',
  'lobby.room_code': 'Raum-Code',
  'lobby.host_badge': 'Host',
  'lobby.countdown': 'Sturm im Anmarsch',
  'lobby.copy_code': 'Code kopieren',
  'lobby.copied': 'Kopiert',
  'lobby.share_hint': 'Teile den Code — deine Crew tritt von der Hafenseite aus bei.',
  'lobby.qr_alt': 'QR-Code zum Beitreten dieses Raums',
  'lobby.qr_hint': 'Zum Beitreten scannen',
  'lobby.slot_open': 'Freier Liegeplatz',
  // „Erste Fahrt"-Willkommenskarte (F4, Batch 0.15.0 — rollen-korrekt per A-Q8:
  // nur die Aktion, die die Rolle wirklich hat; Dismiss setzt nur das Instanz-Flag,
  // nie tw.hints_done).
  'lobby.welcome_host': 'Willkommen, Käpt\'n — drück Start, wenn du bereit bist. Mit dem Raum-Code holst du Crew an Bord.',
  'lobby.welcome_crew': 'Willkommen an Bord — mach dich bereit (Ready); der Käpt\'n setzt die Segel.',

  // ── Result overlay ──
  'result.victory.title': 'Der Sturm bricht — der Hafen hält.',
  'result.defeat.title': 'Der Hafen ist überrannt — die Flut nimmt ihn.',
  'result.abandoned.title': 'Der Hafen wurde verlassen',
  'result.crown': 'Hafenmeister',
  'result.crown_note': 'Ein Hoch auf den Hafenmeister — Status, keine Macht.',
  'result.rematch': 'Revanche',
  'result.stat.bounty': 'Beute',
  'result.stat.kills': 'Erledigt',
  'result.stat.built': 'Gebaut',
  'result.stat.stolen': 'Geplündert',
  'result.reached': 'Welle erreicht',
  'result.duration': 'Dauer',
  'result.difficulty': 'Schwierigkeit',
  'result.back': 'Zurück zum Hafen',
  // Bestmarken-Momente (F3, Batch 0.15.0 — nur coop_survival; A-Q7 truth-conditional:
  // „Neue Bestmarke!" NUR, wenn eine vorherige Best existierte und strikt geschlagen
  // wurde, sonst die stille Zeile. Nur Selbst-Referenz — kein Vergleich/Streak/Verfall/CTA).
  'result.best_new': '⚓ Neue Bestmarke! Welle {n}',
  'result.best_line': 'Bestmarke ({diff}): Welle {n}',
  // Share-Moment (F5, Batch 0.15.0 — user-initiierte Kopie; keine Belohnung, kein Nag, A-Q10).
  'result.share': 'Ergebnis teilen',
  'result.share_copied': 'Link kopiert',
  'result.share_failed': 'Kopieren fehlgeschlagen',
  // Eskalations-Einladung (M1c, Batch 0.20.0 — A-V3, load-bearing): NUR nach echtem
  // Sieg der gerade gespielten Stufe, höchstens bis Hurrikan. Rein deskriptiv —
  // Stufen-Name + bestehende Beschreibung, NIE eine Rekord-Behauptung („noch nie
  // bezwungen" ist faktisch falsch), nie Guilt-Copy gegen die niedrigere Stufe.
  'result.escalate': 'Wenn du raueres Wetter suchst: {name} — {desc}',
  'result.escalate_link': 'Diesen Kurs wählen',

  // ── Result outcome badge (Batch 0.5.0, mode-übergreifend — GAME_DESIGN §15) ──
  'result.outcome.victory': 'Sieg',
  'result.outcome.defeat': 'Niederlage',
  'result.outcome.abandoned': 'Abgebrochen',
  'result.outcome.draw': 'Unentschieden',

  // ── Result: PvP viewer-relative Flavor-Zeilen (Batch 0.5.0) ──
  'result.pvp.victory.title': 'Ihr Hafen ist Asche — die Beute gehört dir.',
  'result.pvp.defeat.title': 'Deine Mauern brachen zuerst — sie nehmen die Beute.',
  'result.pvp.draw.title': 'Beide Häfen hielten — die See nennt es unentschieden.',
  'result.pvp.abandoned.title': 'Das Duell löste sich im Nebel auf.',

  // ── Toasts / banners ──
  'toast.plunder': 'Ein Plünderer schlüpfte durch — {n} Gold aus jeder Verteidiger-Kasse.',
  // Wellen-Sold (R2, Batch 0.20.0 — A-V5): Gegenstück zum Plünder-VERLUST-Toast
  // darüber, gleiche Tonlage. Reine Anzeige (autoritativ bleibt gold_changed),
  // nie Audio auf Ökonomie-Events, keine eskalierende Zelebrations-Kette.
  'toast.sold_paid': 'Die Welle ist abgerechnet — {n} Gold für jeden Verteidiger.',
  'toast.breach': 'Eine Breitseite kracht in den Hafen — {n} Integrität weggerissen.',
  'toast.main_house_hit': 'Räuber erreichten das Haupthaus — {n} Integrität verloren.',
  'toast.structure_destroyed': 'Ein Belagerungskatapult zertrümmerte eine Barrikade — schließ die Bresche.',
  'toast.tsunami': 'Die Flut ist falsch. Die alten Seebären verriegeln die Türen — TSUNAMI IM ANMARSCH.',
  // Batch 0.5.0 — PvP + Anti-Stall (GAME_DESIGN §15 / §2)
  'toast.incoming_send': 'Feindliche Trupps im Anmarsch — sichere deine Mauern.',
  'toast.creep_stalled': 'Zu lange eingepfercht — die Räuber stürmen die Mauern. Die Flut wartet auf niemanden.',
  'toast.pvp_downgraded': 'PvP nimmt gerade Wasser — Co-op-Hafen stattdessen erstellt.',
  'toast.send_launched': 'Trupps ausgelaufen — dein Einkommen ist nun {n}.',
  // Batch 0.12.0 — Gezeitenstraßen (§16)
  'toast.straits_downgraded': 'Die Straßen sind gesperrt — Co-op-Hafen stattdessen erstellt.',
  'toast.dam_inert': 'Der Damm hält — doch die Flotte findet noch eine offene Enge. Sperr einen weiteren Kanal, um den Umweg zu erzwingen.',
  'tide.flood': 'Flut',
  'tide.ebb': 'Ebbe',
  'tide.banner_flood': 'Die Flut drückt nach Osten — die Strömung treibt die Flotten landeinwärts.',
  'tide.banner_ebb': 'Die Ebbe zieht nach Westen — die Strömung reißt die Flotten seewärts.',

  // ── First-Match-Hints (F4, Batch 0.14.0 — nur Co-op, nur Spieler; A5:
  //    nur Mechanik-Existenz, keine komparativen/imperativen Strategie-Aussagen) ──
  'hint.dismiss': 'Schließen',
  'hint.card1': 'Wähle unten eine Küstenverteidigung und setze sie an die Küste — die Flotte kommt von See.',
  'hint.card2': 'Türme feuern von selbst. Jedes versenkte Schiff zahlt die ganze Mannschaft — Ausbauten halten dich dem Sturm voraus.',
  'hint.card3': 'Mauern & Kanäle lenken Landungstruppen um — baue Umwege, gewinne Zeit.',
  // Erste-Welle-überstanden-Toast (F4, Batch 0.15.0 — truth-conditional am echten
  // Welle-1-überstanden-Ereignis, einmal pro Match, nominaler Ton, nie imperativ; A-Q8c).
  'hint.first_wave': 'Der Hafen hält! Zwölf weitere Stürme sind schon unterwegs.',

  // ── Action rejections (reason code → line) ──
  'reject.mode_restricted': 'Diese Verteidigung gehört nicht in diese Straßen.',
  'reject.full_block': 'Der Kriegsrat weigert sich: Jede Bucht hält einen Landungspfad offen.',
  'reject.out_of_bounds': 'Jenseits der Bucht kannst du nicht bauen.',
  'reject.reserved_cell': 'Dieser Grund ist reserviert — Anlandezonen und Haupthaus bleiben frei.',
  'reject.occupied_defense': 'Dort steht bereits eine Verteidigung.',
  'reject.occupied_creep': 'Dort steht ein Landungstrupp — mach den Grund erst frei.',
  'reject.insufficient_gold': 'Nicht genug Gold in der Kasse.',
  'reject.not_owner': 'Nur der Erbauer darf diesen Posten verkaufen oder ausbauen.',
  'reject.max_level': 'Dieser Posten ist bereits auf voller Stärke.',
  'reject.not_found': 'Dieser Posten ist fort.',
  'reject.not_building_phase': 'Die Welle kannst du nur in einer Bau-Pause rufen.',
  'reject.not_connected': 'Du bist nicht verbunden.',
  'reject.not_running': 'Das Match läuft nicht.',
  'reject.no_such_player': 'Du hast keinen Platz in diesem Match.',
  'reject.not_implemented': 'Noch nicht verfügbar.',
  'reject.not_initialized': 'Dieser Hafen ist noch nicht bereit.',
  'reject.match_in_progress': 'Die Schlacht hat begonnen — mitten im Sturm kannst du nicht beitreten.',
  'reject.room_full': 'Dieser Hafen ist voll (4 Verteidiger).',
  'reject.bad_message': 'Das Signal war verstümmelt.',
  'reject.binary_unsupported': 'Nicht unterstütztes Nachrichtenformat.',
  // Batch 0.5.0 — PvP-Send-Ablehnungen (GAME_DESIGN §15)
  'reject.not_in_pvp': 'Im Duell kannst du die Welle nicht rufen — die Sturm-Uhr ist geteilt.',
  'reject.unknown_send': 'Kein solcher Trupp in der Liste des Hafenmeisters.',
  'reject.bad_count': 'Ein Auftrag ist ein Trupp — nicht mehr.',
  'reject.bad_lane': 'Wähle die Nord- oder Süd-Einfahrt.',
  'reject.send_cap_reached': 'Der Hafenmeister heuert höchstens sechs Trupps pro Welle an.',
  'reject.target_not_active': 'Senden geht nur, während die Welle des Gegners läuft.',

  // ── Wave banners (13 Wellen × title/flavor; Chronicle-Register, ≤ 90 Zeichen) ──
  'wave.1.title': 'Späher am Horizont',
  'wave.1.flavor': 'Erst die Späher, immer. Sie zählen deine Mauern, bevor sie die Stiefel nässen.',
  'wave.2.title': 'Die Slups wittern Gold',
  'wave.2.flavor': 'Die Slups wittern Gold im Wind — und Slups irren sich selten.',
  'wave.3.title': 'Speere in der Brandung',
  'wave.3.flavor': 'Der Strand gehört dem, der noch steht, wenn die Brandung zurückweicht.',
  'wave.4.title': 'Sie kommen für deine Kassen',
  'wave.4.flavor': 'Sie sind nicht wegen der Insel hier. Sie sind wegen der Kassen hier.',
  'wave.5.title': 'Schwere Eiche auf der Flut',
  'wave.5.flavor': 'Schwere Eiche auf der Flut — langsam, schwerfällig und stur beim Sinken.',
  'wave.6.title': 'Ein Schwarm mit Schleudern',
  'wave.6.flavor': 'Ein Schwarm mit Schleudern. Kein Stein zählt für sich — das ist das Tückische am Schwarm.',
  'wave.7.title': 'Die See erhebt sich gegen uns alle',
  'wave.7.flavor': 'Die Flut ist falsch. Die See erhebt sich gegen uns alle — beide Einfahrten zugleich.',
  'wave.8.title': 'Rollender Donner an Land',
  'wave.8.flavor': 'Rollender Donner kommt an Land. Ein fliegender Fels liest keine Friedensverträge.',
  'wave.9.title': 'Die Linie aus Eiche',
  'wave.9.flavor': 'Eine Linie aus Eiche auf dem Wasser. Brich die Eskorte, oder sie bricht deinen Strand.',
  'wave.10.title': 'Brückenkopf!',
  'wave.10.flavor': 'Brückenkopf. Fünf Rümpfe treffen zugleich den Sand — die Brandung hält sie nicht.',
  'wave.11.title': 'Beutelschneider im Rauch',
  'wave.11.flavor': 'Beutelschneider im Rauch. Sie nehmen das Gold und verabschieden sich lautstark.',
  'wave.12.title': 'Die Flotte sammelt sich',
  'wave.12.flavor': 'Die ganze Flotte sammelt sich vor der Küste. Das ist das Klopfen, bevor die Tür fällt.',
  'wave.13.title': 'Die Schwarze Flotte',
  'wave.13.flavor': 'Schwarze Segel, rote Decks — die Schwarze Flotte selbst. Versenk sie oder schweig für immer.',

  // ── Damage-type + target badges (shop cards) ──
  'damage.arrow': 'Pfeil',
  'damage.bolt': 'Bolzen',
  'damage.stone': 'Stein',
  'damage.pitch': 'Pech',
  'target.sea_land': 'See + Land',
  'target.land': 'Land',
  'target.none': 'Mauer',

  // ── Defense lore tooltips (GAME_DESIGN §3 tone; catapult carries ADR-005) ──
  'defense.cliff_archers.lore': 'Billige Augen und flinke Bögen auf den Klippen — sie lichten die Leichten, nicht die Gepanzerten.',
  'defense.ballista_battery.lore': 'Ein Bolzen, der Eiche wie Panzer durchschlägt. Lädt langsam, lässt sich schwer abschütteln.',
  'defense.catapult_emplacement.lore': 'Wirft Stein in die Rudel am Strand. Gegen rollende Wellen zu ungenau — nur Landziele.',
  'defense.pitch_kettle.lore': 'Siedendes Pech für die Engstellen — es bremst den Sturm und brennt weiter.',
  'defense.harbor_barricade.lore': 'Eine billige Pfahlwand. Sie formt das Labyrinth; die See muss den langen Weg gehen.',
  'defense.tide_dam.lore': 'Geröll und Balken würgen eine Untiefe. Die Strömung biegt sich darum — und mit ihr die Flotte.',

  // ── Creep lore tooltips (preview chips; STORY-LINE §3/§4 tone) ──
  'creep.small_warship.lore': 'Eine wendige Riffpatrouille — feuert eine Breitseite auf den Kai und dreht ab.',
  'creep.large_warship.lore': 'Eine schwimmende Eichenfestung, träge und schwerfällig, mit einer Breitseite dazu.',
  'creep.landing_boat.lore': 'Eine prall gefüllte Barke. Versenke sie auf See, sonst betreten ihre Truppen deinen Strand.',
  'creep.stone_thrower.lore': 'Schleudern und Verzweiflung — eine hastige Miliz, die ein Haupthaus dennoch dellt.',
  'creep.spearman.lore': 'Der gepanzerte Schildwall des Sandes. Pfeile prallen ab; Bolzen nicht.',
  'creep.plunderer.lore': 'Schnell und leicht — er nimmt Gold, keine Leben, und verabschiedet sich lautstark.',
  'creep.siege_catapult.lore': 'Ein rollendes Uhrwerk der Zerstörung. Es mahlt Barrikaden zu Splittern.',
  'creep.flagship.lore': 'Die Schwarze Flotte selbst — schwarze Segel, rote Decks, eine Breitseite, die Logbücher beendet.',

  // ── Preview markers ──
  'hud.preview_lands': 'landet',
  'hud.crown_title': 'Höchste Beute',

  // ── Reconnect overlay ──
  'reconnect.title': 'Die Leine riss — wir werfen erneut aus…',
  'reconnect.done': 'Leine in {ms} ms wiederhergestellt.',

  // ════════════════════════════════════════════════════════════════════════
  // Meta-Surface (Batch 0.4.0) — vollständige DE-Parität (F2/P9, test-erzwungen)
  // ════════════════════════════════════════════════════════════════════════

  'nav.changelog': 'Logbuch',
  'nav.feedback': 'Feedback',
  'meta.title.home': 'TideWars — Echtzeit-Koop-Tower-Defense',
  'meta.title.play': 'Kurs wählen — TideWars',
  'meta.title.changelog': 'Logbuch — TideWars',
  'meta.title.feedback': 'Feedback — TideWars',
  'meta.title.room_not_found': 'Diese Bucht ist leer — TideWars',
  'meta.title.match': 'TideWars — Hafenverteidigung',
  'meta.desc.home':
    'Eine kostenlose Echtzeit-Koop-Tower-Defense im Kampfinsel-Universum. Direkt rein in kurze Hafenverteidigungs-Matches — ohne Account, ohne Download, ohne persistente Welt.',
  'meta.desc.changelog':
    'Jeder überstandene Sturm und jede geflickte Planke in TideWars — das laufende Logbuch, neueste zuerst.',
  'meta.desc.feedback':
    'Teile eine Idee, einen Fehler oder eine Balance-Notiz zu TideWars — Spieler-Signal, das das Design lenkt.',

  // ── Share-Moment (F5, Batch 0.15.0 — /s Schlachtbericht, noindex). A-Q9: neutral,
  //    selbstberichtet, GET-only-CTA; NIE Challenge-/Vergleichs-Copy, NIE „überstanden"
  //    bei einer Niederlage (outcome-korrekte Aussage). Titel = Aussage + Marke. ──
  'meta.title.share': '{stmt} — TideWars',
  'share.description':
    'Ein geteiltes Hafenverteidigungs-Ergebnis aus TideWars — eine kostenlose Echtzeit-Koop-Tower-Defense. Ohne Account, ohne Download.',
  'share.eyebrow': 'Schlachtbericht',
  'share.stmt_victory': 'Alle 13 Wellen auf {diff} überstanden',
  'share.stmt_defeat': 'Welle {n} von 13 auf {diff} erreicht',
  'share.self_reported': 'Ein selbstberichtetes Hafenverteidigungs-Ergebnis.',
  'share.cta': 'Gründe deinen eigenen Hafen',

  'landing.private_label': 'Diesen Hafen privat machen (aus der Liste verbergen)',
  // A3 (Batch 0.6.0): nennt den neuen Zuschau-Aspekt am Wirkort — „öffentlich"
  // heißt jetzt gelistet UND ab Sturmbeginn live zuschaubar.
  'landing.public_wirkort':
    'Dein Hafen erscheint in der Liste auf der Startseite — jeder kann ankern und beitreten und ihn ab Sturmbeginn live verfolgen.',

  'lobby.list_heading': 'Offene Häfen',
  'lobby.list_empty': 'Noch keine Crews auf See — gründe den ersten Hafen und teile den Code.',
  'lobby.list_join': 'Beitreten',
  'lobby.kick': 'Entfernen',

  // ── Live-Zuschauen (Batch 0.6.0 — ehrliche Zahlen, keine Scarcity, Pin P2) ──
  'live.heading': 'Schlachten im Gange',
  'live.watch': 'Zuschauen',
  'live.wave_progress': 'Welle {n}/13',
  'live.spectator_count': '👁 {n}',
  'spectate.live_badge': 'LIVE',
  'spectate.nudge_start': 'Gründe deinen eigenen Hafen',
  'spectate.ended': 'Die Schlacht ist vorbei — die Flut hat sich gewendet.',
  'spectate.unavailable': 'Hier gibt es keine laufende Schlacht — sie ist wohl vorbei oder anderswo unterwegs.',

  'crosspromo.eyebrow': 'Aus demselben Universum',
  'crosspromo.copy':
    'Kampfinsel ist eine persistente Inselwelt langsamer, weiter Seereisen. TideWars ist ein eigenes Spiel — kurze, in sich geschlossene Matches mit eigenem Fortschritt und ohne persistente Welt.',
  'crosspromo.link': 'Kampfinsel — die persistente Inselwelt',
  'crosspromo.banner_alt': 'Kampfinsel — persistente Inselwelt aus demselben Universum.',

  'changelog.heading': 'Logbuch',
  'changelog.intro':
    'Jeder Sturm, den wir überstanden, und jede Planke, die wir geflickt haben — neueste zuerst.',
  'changelog.entries_en_notice': 'Einträge sind derzeit auf Englisch.',
  'changelog.empty': 'Das Logbuch ist noch leer.',
  'changelog.cat.added': 'Neu',
  'changelog.cat.changed': 'Geändert',
  'changelog.cat.fixed': 'Behoben',
  'changelog.cat.removed': 'Entfernt',

  'feedback.heading': 'Feedback',
  'feedback.intro': 'Sag uns, was den Hafen besser machen würde.',
  'feedback.field_type': 'Typ',
  'feedback.field_title': 'Kurzfassung',
  'feedback.field_details': 'Details (optional)',
  'feedback.title_placeholder': 'Eine Zeile — worum geht es?',
  'feedback.details_placeholder': 'Alles, was uns hilft, es zu verstehen.',
  'feedback.submit': 'Feedback senden',
  'feedback.list_heading': 'Was die Crew sagt',
  'feedback.expectation': 'Das ist Signal für die Design-Entscheidung — keine Abstimmung, die sie trifft.',
  'feedback.empty': 'Noch kein Feedback — trag die erste Notiz ein.',
  'feedback.type.idea': 'Idee',
  'feedback.type.bug': 'Fehler',
  'feedback.type.balance': 'Balance',
  'feedback.upvote': 'Zustimmen',
  'feedback.downvote': 'Ablehnen',
  'feedback.vote_locked': 'Voten kannst du nach deinem ersten Match.',
  'feedback.notice.submitted': 'Eingetragen — danke für das Signal.',
  'feedback.notice.rate_limited': 'Du hast heute schon einiges eingetragen — versuch es morgen wieder.',
  'feedback.notice.invalid': 'Diese Notiz ließ sich nicht eintragen — prüfe die Felder und versuch es erneut.',
  'feedback.notice.voted': 'Deine Stimme ist gezählt.',
  'feedback.notice.vote_locked': 'Voten kannst du nach deinem ersten Match.',
  'feedback.notice.vote_rate_limited': 'Das sind viele Stimmen für einen Tag — versuch es morgen wieder.',
  // Turnstile-Reject-Notices (Batch 0.11.0, A-TS4) — der Re-Render erhält die
  // getippten Felder, deshalb beide Zeilen mit „Deine Notiz ist noch da" (nüchtern).
  'feedback.notice.captcha_invalid': 'Der Sicherheits-Check wurde nicht rechtzeitig fertig — bitte erneut absenden. Deine Notiz ist noch da.',
  'feedback.notice.captcha_missing':
    'Der Sicherheits-Check konnte nicht laden (womöglich blockiert eine Browser-Erweiterung ihn). Deine Notiz ist noch da.',

  'time.just_now': 'gerade eben',
  'time.minutes': 'vor {n} Min',
  'time.hours': 'vor {n} Std',
  'time.days': 'vor {n} T',

  'reject.kicked': 'Der Hafenmeister hat deinen Liegeplatz geräumt — du hast diesen Hafen verlassen.',
  'reject.join_paused': 'Der Hafenmeister hat gerade einen Liegeplatz geräumt — versuch es gleich noch einmal.',

  // ════════════════════════════════════════════════════════════════════════
  // Account / Auth v1 (Batch 0.9.0) — vollständige DE-Parität (test-erzwungen)
  // ════════════════════════════════════════════════════════════════════════
  'nav.account': 'Kapitän',
  'meta.title.account': 'Dein Kapitän — TideWars',
  'account.heading': 'Kapitänskajüte',
  'account.intro': 'Ein Name am Ruder, den sich die Crew merkt — ohne E-Mail, ohne Download.',
  'account.login.heading': 'Zurück ans Ruder',
  'account.login.button': 'Anmelden',
  'account.register.heading': 'Beanspruche deinen Kapitänsnamen',
  'account.register.button': 'Beanspruchen',
  'account.register.nickname_rule': '3–24 Zeichen: Buchstaben, Ziffern, - und _ — keine Leerzeichen.',
  'account.register.disclosure':
    'Keine E-Mail nötig — und keine erhoben. Einen Passwort-Reset gibt es noch nicht, also gut aufbewahren.',
  'account.field.nickname': 'Kapitänsname',
  'account.field.password': 'Passwort',
  'account.signed_in_as': 'Am Ruder:',
  'account.member_since': 'An Bord seit',
  'account.logout': 'Abmelden',
  'account.err.invalid_nickname': 'Dieser Name geht nicht — 3–24 Buchstaben, Ziffern, - oder _, und nichts Reserviertes.',
  'account.err.nickname_taken': 'Ein anderer Kapitän segelt bereits unter diesem Namen.',
  'account.err.weak_password': 'Dein Passwort braucht mindestens 8 Zeichen (und höchstens 72 Bytes).',
  'account.err.bad_credentials': 'Name und Passwort stehen so nicht im Logbuch.',
  'account.err.rate_limited': 'Zu viele Versuche aus deinem Netzwerk — versuch es später erneut.',
  'account.msg.registered': 'Willkommen an Bord, Kapitän — dein Name ist beansprucht.',

  // ════════════════════════════════════════════════════════════════════════
  // Fleet Intelligence (Zahlen-Transparenz im Match) — vollständige DE-Parität
  // ════════════════════════════════════════════════════════════════════════
  'stat.dps': 'DPS',
  'stat.range': 'Reichweite',
  'stat.cells': 'Zellen',
  'stat.cells_per_s': 'Zellen/s',
  'stat.hp': 'HP',
  'stat.damage_shot': 'Schaden/Schuss',
  'stat.fire_rate': 'Feuerrate',
  'stat.pierce': 'Durchschlag',
  'stat.aoe': 'Flächenradius',
  'stat.slow': 'Verlangsamung',
  'stat.burn': 'Brand',
  'stat.structure_hp': 'Struktur-HP',
  'stat.contact': 'Kontakt/s',
  'stat.vs_light': 'vs. leicht',
  'stat.vs_armored': 'vs. gepanzert',
  // Qualitativer Counter-Wortlaut (R4, Batch 0.20.0 — A-V6a): zur LAUFZEIT aus der
  // Live-Schadensmatrix abgeleitet (Schwellen über den Multiplikatoren), nie je Turm
  // hartkodiert — ein KV-`config:balance`-Patch darf die Karte nicht lügen lassen.
  'stat.counter_strong': 'Stark',
  'stat.counter_weak': 'Schwach',
  'stat.counter_even': 'Neutral',
  'stat.payload': 'Fracht',
  'stat.broadside': 'Breitseite',
  'stat.breach': 'Durchbruch',
  'stat.steals_gold': 'Stiehlt Gold',
  'armor.light': 'Leichte Rüstung',
  'armor.armored': 'Schwere Rüstung',
  'tower.upgrade_preview': '→ L{lvl}: {dmg} Schaden · {rate}/s',
  'tower.upgrade_preview_hp': '→ L{lvl}: {hp} HP',
  'tower.built_by': 'Gebaut von {name} — nur der Erbauer kann diesen Posten ausbauen oder verkaufen.',
  'result.stat.damage': 'Schaden',
  'hud.send_payback': 'amortisiert in ~{n} Wellen',
  'send.payback_wirkort': 'Einkommen fließt nur an deinen eigenen Wellen-Enden, bis Welle 13.',

  // ════════════════════════════════════════════════════════════════════════
  // Befehle des Käpt'ns + Zielwahl (Release 0.21.0, Gate-G) — vollständige DE-
  // Parität; vor-Schießpulver (Feuertöpfe, brennender Teer, Kriegstrommel).
  // ════════════════════════════════════════════════════════════════════════
  'order.volley.name': 'Feuertopf-Salve',
  'order.tar_trap.name': 'Teerfalle',
  'order.rally.name': 'Gefechtstrommel',
  'order.volley.short': 'Salve',
  'order.tar_trap.short': 'Teer',
  'order.rally.short': 'Trommel',
  'order.volley.tip':
    'Feuertöpfe fliegen von den Hafenmauern und zerbersten nach {delay} s: {dmg} Schaden an jedem Räuber im Ring von {r} Zellen, danach {dps} Brand pro Sekunde für {dur} s.',
  'order.tar_trap.tip':
    'Brennender Teer auf freiem Grund, scharf nach {arm} s: Der erste Räuber, der hineintritt, entzündet ihn — {dmg} Schaden im Ring von {r} Zellen plus {dps} Brand pro Sekunde für {dur} s; bis zu {max} dürfen zugleich liegen.',
  'order.rally.tip': 'Die Kriegstrommel rollt: Jeder Turm im Umkreis von {r} Zellen feuert {dur} s lang ×{mult} so schnell.',
  'orders.bar_label': "Befehle des Käpt'ns",
  'orders.unlock_short': 'W{n}',
  'orders.storm_locked':
    'Die Flut löscht die Feuertöpfe, wäscht den Teer fort und ertränkt die Trommeln — diese Welle keine Befehle.',
  'orders.storm_warning':
    'Die kommende Flut löscht die Feuertöpfe, wäscht den Teer fort und ertränkt die Trommeln — in dieser Welle keine Befehle.',
  'orders.aim.mouse': '{name}: Ziel anklicken — Esc oder Rechtsklick bricht ab.',
  'orders.aim.touch': '{name}: Ziel antippen, dann zum Bestätigen erneut antippen.',
  'orders.tap_confirm': 'Tippe dieselbe Stelle erneut an, um den Befehl zu geben.',
  'orders.reason.locked': '{name} wird ab Welle {n} freigeschaltet.',
  'orders.reason.phase.volley': 'Feuertöpfe fliegen nur, während eine Welle läuft.',
  'orders.reason.phase.tar_trap': 'Teer lässt sich nur legen, während das Match läuft.',
  'orders.reason.phase.rally': 'Die Kriegstrommel erklingt nur, während eine Welle läuft.',
  'orders.reason.spent': '{name} ist für diese Welle eingesetzt — die nächste Welle bringt eine frische Ladung.',
  'orders.reason.rearming': 'Die Crew rüstet noch nach — {name} ist gleich wieder bereit.',
  'orders.reason.trap_limit':
    'Alle {max} deiner Teerfallen liegen — bau eine ab (anklicken) oder lass eine zünden, um eine weitere zu legen.',
  'orders.reason.target.volley': 'Feuertöpfe landen nur in der Bucht oder auf ihren Seewegen.',
  'orders.reason.target.tar_trap': 'Teer hält nur auf freiem Grund, den die Räuber begehen können.',
  'orders.reason.target.rally': 'Die Kriegstrommel braucht mindestens einen Turm in Hörweite.',
  'orders.reason.reserved': 'Teer bleibt von den Anlandezonen und dem Haupthaus fern.',
  'orders.reason.occupied_defense': 'Dort steht eine Verteidigung — Teer braucht freien Grund.',
  'orders.reason.occupied_trap': 'Dort liegt bereits eine Teerfalle.',
  'orders.reason.mode': "In den Gezeitenstraßen bleiben die Befehle des Käpt'ns im Hafen.",
  'targeting.label': 'Ziel',
  'targeting.first': 'Erster',
  'targeting.last': 'Letzter',
  'targeting.highest_hp': 'Stärkster',
  'targeting.first.tip':
    'Erster — zielt auf den Räuber, der dem Durchbruch am nächsten ist; Küstenposten nehmen zuerst Schiffe.',
  'targeting.last.tip': 'Letzter — zielt auf den Räuber, der am weitesten vom Durchbruch entfernt ist.',
  'targeting.highest_hp.tip':
    'Stärkster — zielt auf den Räuber mit dem meisten Rumpf; an der Küste ist das oft das Landungsboot, nicht das Kriegsschiff.',
  'targeting.read_only': 'Nur der Erbauer darf das Ziel dieses Postens festlegen.',
  'targeting.none': 'Dieser Posten hat kein Ziel zu wählen.',
  'targeting.mode': 'In den Gezeitenstraßen zielt jeder Posten auf das vorderste Schiff.',
  'trap.arming': 'Wird scharf…',
  'trap.armed': 'Scharf — der erste Räuber, der hineintritt, entzündet sie.',
  'trap.dormant': 'Ruht — die Flut hat sie bis zur nächsten Welle gelöscht.',
  'trap.remove': 'Abbauen (kostenlos)',
  'trap.laid_by': 'Gelegt von {name}.',
  'trap.gone': 'Diese Teerfalle ist schon fort.',
  'trap.not_owner': 'Nur wer diese Falle gelegt hat, kann sie abbauen.',
  'hint.order.volley':
    "Befehl des Käpt'ns bereit: die Feuertopf-Salve — wähle sie in der Befehlsleiste und dann einen Punkt; die Töpfe zerbersten über allem im Ring.",
  'hint.order.tar_trap':
    'Neuer Befehl: die Teerfalle — auf freiem Grund gelegt, fängt sie unter dem ersten Räuber Feuer und verbrennt das Rudel um ihn herum. Sie wartet, bis sie zündet.',
  'hint.order.rally':
    'Neuer Befehl: Gefechtstrommel — die Kriegstrommel lässt jeden Turm in Hörweite ein paar Sekunden schneller feuern.',
  'lobby.straits_orders':
    "In den Gezeitenstraßen bleiben die Befehle des Käpt'ns im Hafen — hier entscheiden Dämme und Türme.",
  'reject.order_locked': 'Dieser Befehl wird erst in einer späteren Welle freigeschaltet.',
  'reject.storm_locked':
    'Die Flut löscht die Feuertöpfe, wäscht den Teer fort und ertränkt die Trommeln — diese Welle keine Befehle.',
  'reject.policy_unavailable': 'Dieses Ziel ist für diesen Posten nicht verfügbar.',
  'reject.not_wave_phase': 'Dieser Befehl wirkt nur, während eine Welle läuft.',
  'reject.no_charges': 'Dieser Befehl ist für diese Welle eingesetzt — die nächste Welle bringt eine frische Ladung.',
  'reject.order_rearming': 'Die Crew rüstet noch nach — einen Moment.',
  'reject.invalid_target': 'Dort kann der Befehl nicht landen.',
  'reject.trap_limit':
    'Alle deine Teerfallen liegen — bau eine ab (anklicken) oder lass eine zünden, um eine weitere zu legen.',
  'reject.occupied_trap': 'Dort liegt eine Teerfalle — wer sie gelegt hat, kann sie kostenlos abbauen.',
};

const dictionaries: Record<Locale, Dictionary> = { en, de };

export function t(locale: Locale, key: string): string {
  return dictionaries[locale][key] ?? dictionaries[DEFAULT_LOCALE][key] ?? key;
}

/** Template-Füller: ersetzt `{token}`-Platzhalter (i18n bleibt interpolationsfrei). */
export function format(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

export function pickLocale(request: Request): Locale {
  const header = request.headers.get('accept-language') ?? '';
  return header.toLowerCase().startsWith('de') ? 'de' : DEFAULT_LOCALE;
}

/** Locale aus einem String validieren (Client liest sie aus einem data-Attribut). */
export function parseLocale(value: string | null | undefined): Locale {
  return value === 'de' ? 'de' : DEFAULT_LOCALE;
}
